import {makeStyles} from '@material-ui/core/styles';
import firebase from 'firebase/app';
import 'firebase/auth';
import React, {useEffect, useState} from 'react';
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import ConfirmationDialog from "../../dialogs/confirmation";
import Container from "@material-ui/core/Container";
import CssBaseline from "@material-ui/core/CssBaseline";
import usePersistentStore from "../../persistentStore";
import {useMutation} from "@apollo/react-hooks";
import {DELETE_USER} from "../../database/queries/userQueries";
import {notify} from "../../utils/notify";
import TextField from "@material-ui/core/TextField";
import {useHistory} from "react-router-dom";

const useStyles = makeStyles(theme => ({
    paper: {
        marginTop: theme.spacing(4),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        '& strong': {
            color: 'red',
        }
    },
    button: {
        marginTop: theme.spacing(4),
    },
}));

const DestroyAccount = props => {
    const classes = useStyles();
    const [confirmed, setConfirmed] = useState(false);
    const [destroyed, setDestroyed] = useState(false);
    const [globalPersistentState, globalPersistentActions] = usePersistentStore();
    const [credentials, setCredentials] = useState({email: '', password: ''});
    const [reauthenticated, setReauthenticated] = useState(false);
    const [confirmation, setConfirmation] = useState({});
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const history = useHistory();

    const [deleteUser, {error: deleteUserError}] = useMutation(DELETE_USER);

    const destroy = () => {
        deleteUser({variables: {deleteUserId: globalPersistentState.me.userId}}).then(deleteUserResult => {
            if (deleteUserResult.data.deleteUser.returnCode === 0) {
                notify('deleteUserData', 'warning', 'Destroyed user data');
                firebase.auth().currentUser.delete().then(function () {
                    setDestroyed(true);
                    notify('deleteUserAccount', 'warning', 'Destroyed user account', false, 'destroyed-user-account');
                    globalPersistentActions.clearStorage();
                    console.log('## SVR destroyAccount ## changing history', '/sign-in');
                    history.push('/sign-in')
                }).catch(function (error) {
                    notify('deleteUserAccount', 'error', 'Some problem destroying user account');
                });
            }
        }).catch(e => {
            notify('deleteUserData', 'error', 'Some problem destroying user data');
        })
    };

    const handleReauthenticateClick = () => {
        try {
            const user = firebase.auth().currentUser;
            const creds = firebase.auth.EmailAuthProvider.credential(
                credentials.email,
                credentials.password
            );

            user.reauthenticateWithCredential(creds).then(function () {

                // firebase.auth().signInWithEmailAndPassword(credentials.email, credentials.password).then(function () {
                setReauthenticated(true);
                destroy();
            }).catch(function (error) {
                notify('deleteUserData', 'error', 'Could not reauthenticate user');
            });
        } catch (e) {
            console.trace(e);
        }
    };

    const question = 'Are you sure you want to completely destroy the account for the user named ' + globalPersistentState.me.displayName + ' with email ' + globalPersistentState.me.email + '?  Again, this cannot be undone and all of your data will be lost.'
    const handleDestroyClick = () => {
        setConfirmation({
            title: "Destroy My Account",
            question: question,
            yesHandler: () => {
                setConfirmationOpen(false);
                setConfirmed(true);
            },
            noHandler: () => {
                setConfirmationOpen(false);
                notify('deleteUserData', 'info', 'Whew! Not destroying this user today!');
            },
        });
        setConfirmationOpen(true);
    };

    useEffect(() => {
        if (deleteUserError) notify('deleteUserError', 'error', deleteUserError.message);
    }, [deleteUserError]);

    const onChange = event => {
        setCredentials({
            ...credentials,
            [event.target.name]: event.target.value
        });
    };

    const isInvalid = credentials.password === '' || credentials.email !== globalPersistentState.me.email;

    return (
        <Container component="main" maxWidth="xs">
            <CssBaseline/>
            <div className={classes.paper}>
                <Typography component="h6" variant="h6" className={classes.sectionTitle}>
                    Destroy Account
                </Typography>
            </div>
            <div className={classes.paper}>
                {!confirmed && <div>
                    <Typography style={{textAlign: 'center',}}>Completely destroy the account
                        for user <strong>{globalPersistentState.me.displayName}</strong> with
                        email <strong>{globalPersistentState.me.email}</strong>? This
                        cannot be undone and all of your data will be lost!</Typography>
                    <div className={classes.button} style={{textAlign: 'center',}}>
                        <Button
                            id="destroy-button"
                            variant="contained"
                            onClick={handleDestroyClick}
                            color="primary"
                            autoFocus
                            style={{textAlign: 'center',}}
                        >
                            Destroy!
                        </Button>
                    </div>
                </div>}

                {credentials && confirmed && !reauthenticated && <div>
                    <form className={classes.form} noValidate>
                        <TextField
                            variant="outlined"
                            aria-label="sign-in-email"
                            margin="dense"
                            required
                            fullWidth
                            id="email"
                            onChange={onChange}
                            value={credentials.email}
                            label="Verify Email Address"
                            placeholder="Verify Email Address"
                            name="email"
                            autoComplete="email"
                            autoFocus
                        />
                        <TextField
                            variant="outlined"
                            aria-label="sign-in-password"
                            margin="dense"
                            required
                            fullWidth
                            name="password"
                            onChange={onChange}
                            value={credentials.password}
                            label="Password"
                            placeholder="Password"
                            type="password"
                            id="password"
                            autoComplete="current-password"
                        />
                        <Button
                            aria-label="reauthenticate"
                            fullWidth
                            id="destroy-account-button"
                            name="destroy-account-button"
                            disabled={isInvalid}
                            variant="contained"
                            color="primary"
                            onClick={handleReauthenticateClick}
                        >
                            Destroy Account
                        </Button>
                    </form>
                </div>
                }

                {confirmed && reauthenticated && !destroyed &&
                <Typography style={{textAlign: 'center',}}>
                    Destroying account
                    for {globalPersistentState.me.displayName} with
                    email {globalPersistentState.me.email}
                </Typography>
                }

                {confirmed && destroyed &&
                <Typography style={{textAlign: 'center',}}>Account destroyed</Typography>
                }
            </div>

            <ConfirmationDialog
                open={confirmationOpen}
                title={confirmation.title}
                question={confirmation.question}
                handleYes={confirmation.yesHandler}
                handleNo={confirmation.noHandler}
            />
        </Container>
    )
};

export default DestroyAccount;
